import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, Banknote, GraduationCap, Mail, MapPin, Phone, UserRound } from 'lucide-react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes
} from 'firebase/storage';
import { db, storage } from '../../core/firebase/firebase';

function displayValue(value, fallback = 'Non renseigné') {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
}

function displayDate(value) {
  if (!value) return 'Non renseignée';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return displayValue(value);
  return new Intl.DateTimeFormat('fr-FR').format(date);
}

function teacherName(teacher) {
  return teacher?.fullname || teacher?.fullName || teacher?.username || teacher?.email || 'Enseignant';
}

function genderLabel(value) {
  if (value === 'female') return 'Féminin';
  if (value === 'male') return 'Masculin';
  return displayValue(value);
}

function maritalStatusLabel(notice) {
  const labels = {
    single: 'Célibataire',
    married: 'Marié(e)',
    divorced: 'Divorcé(e)',
    widowed: 'Veuf / veuve',
    other: notice?.maritalStatusOther || 'Autre'
  };
  return labels[notice?.maritalStatus] || 'Non renseignée';
}

export default function TeacherDetailPage() {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timetableUrl, setTimetableUrl] = useState('');
const [timetableLoading, setTimetableLoading] = useState(false);
const [timetableUploading, setTimetableUploading] = useState(false);
const [timetableMessage, setTimetableMessage] = useState('');

  useEffect(() => {
    let active = true;

    async function loadTeacher() {
      setLoading(true);
      setError('');
      try {
        const [teacherSnapshot, assignmentsSnapshot, classesSnapshot, subjectsSnapshot] = await Promise.all([
          getDoc(doc(db, 'users', teacherId)),
          getDocs(collection(db, 'teacherAssignments')),
          getDocs(collection(db, 'classes')),
          getDocs(collection(db, 'subjects'))
        ]);

        if (!teacherSnapshot.exists()) throw new Error('Enseignant introuvable.');
        if (!active) return;

        setTeacher({ id: teacherSnapshot.id, ...teacherSnapshot.data() });
        setAssignments(
          assignmentsSnapshot.docs
            .map((item) => ({ id: item.id, ...item.data() }))
            .filter((item) => item.teacherId === teacherId && item.status === 'active')
        );
        setClasses(classesSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setSubjects(subjectsSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      } catch (loadError) {
        console.error('Dossier enseignant :', loadError);
        if (active) setError(loadError.message || 'Impossible de charger le dossier de cet enseignant.');
      } finally {
        if (active) setLoading(false);
      }
    }

    loadTeacher();
    return () => { active = false; };
  }, [teacherId]);

  useEffect(() => {
  let cancelled = false;

  async function loadTeacherTimetable() {
    if (!teacherId) {
      setTimetableUrl('');
      return;
    }

    setTimetableLoading(true);
    setTimetableMessage('');

    try {
      const fileRef = ref(
        storage,
        `teacherTimetables/${teacherId}/emploi`
      );

      const url = await getDownloadURL(fileRef);

      if (!cancelled) {
        setTimetableUrl(url);
      }
    } catch (err) {
      if (err?.code === 'storage/object-not-found') {
        if (!cancelled) {
          setTimetableUrl('');
        }
      } else {
        console.error('Chargement emploi enseignant :', err);

        if (!cancelled) {
          setTimetableMessage(
            "Impossible de charger l'emploi du temps."
          );
        }
      }
    } finally {
      if (!cancelled) {
        setTimetableLoading(false);
      }
    }
  }

  loadTeacherTimetable();

  return () => {
    cancelled = true;
  };
}, [teacherId]);

async function uploadTeacherTimetable(event) {
  const file = event.target.files?.[0];

  event.target.value = '';

  if (!file || !teacherId) return;

  const allowedTypes = [
    'image/png',
    'image/jpeg'
  ];

  if (!allowedTypes.includes(file.type)) {
    setTimetableMessage(
      'Veuillez sélectionner un fichier PNG, JPG ou JPEG.'
    );
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    setTimetableMessage(
      'Le fichier ne doit pas dépasser 10 Mo.'
    );
    return;
  }

  setTimetableUploading(true);
  setTimetableMessage('');

  try {
    const fileRef = ref(
      storage,
      `teacherTimetables/${teacherId}/emploi`
    );

    await uploadBytes(fileRef, file, {
      contentType: file.type,
      cacheControl: 'no-cache,max-age=0,must-revalidate'
    });

    const url = await getDownloadURL(fileRef);

    setTimetableUrl(url);
    setTimetableMessage(
      "Emploi du temps de l'enseignant enregistré."
    );
  } catch (err) {
    console.error(
      'Import emploi du temps enseignant :',
      err
    );

    setTimetableMessage(
      "Impossible d'importer l'emploi du temps."
    );
  } finally {
    setTimetableUploading(false);
  }
}

async function removeTeacherTimetable() {
  if (!teacherId) return;

  const confirmed = window.confirm(
    "Supprimer l'emploi du temps de cet enseignant ?"
  );

  if (!confirmed) return;

  setTimetableMessage('');

  try {
    const fileRef = ref(
      storage,
      `teacherTimetables/${teacherId}/emploi`
    );

    await deleteObject(fileRef);

    setTimetableUrl('');
    setTimetableMessage(
      "Emploi du temps de l'enseignant supprimé."
    );
  } catch (err) {
    console.error(
      'Suppression emploi enseignant :',
      err
    );

    setTimetableMessage(
      "Impossible de supprimer l'emploi du temps."
    );
  }
}

  const assignmentDetails = useMemo(() => assignments.map((item) => ({
    ...item,
    className: classes.find((entry) => entry.id === item.classId)?.name || item.classId || 'Classe',
    subjectName: subjects.find((entry) => entry.id === item.subjectId)?.name || item.subjectId || 'Matière'
  })), [assignments, classes, subjects]);

  if (loading) return <section className="teacher-detail-page-v2"><div className="panel">Chargement du dossier…</div></section>;
  if (error) return <section className="teacher-detail-page-v2"><div className="alert alert-error">{error}</div></section>;
  if (!teacher) return null;

  const notice = teacher.teacherNotice;
  const completed = Boolean(notice && (teacher.teacherOnboardingCompletedAt || notice.completedAt));

  return (
    <section className="teacher-detail-page-v2">
      <button type="button" className="teacher-detail-back-v2" onClick={() => navigate('/personnel?view=teachers')}>
        <ArrowLeft size={18} /> Retour à l’équipe pédagogique
      </button>

      <div className="teacher-detail-hero-v2">
        <div className="teacher-detail-avatar-v2">{teacherName(teacher).charAt(0).toUpperCase()}</div>
        <div className="teacher-detail-hero-copy-v2">
          <p className="eyebrow">ÉQUIPE PÉDAGOGIQUE</p>
          <h1>{teacherName(teacher)}</h1>
          <div className="teacher-detail-meta-v2">
            <span><UserRound size={16} /> {displayValue(teacher.username)}</span>
            <span><Mail size={16} /> {displayValue(teacher.personalEmail || teacher.email)}</span>
            <span><Phone size={16} /> {displayValue(teacher.phone)}</span>
          </div>
        </div>
        <span className={`teacher-admin-profile-status-v1 ${completed ? 'is-complete' : 'is-pending'}`}>
          <BadgeCheck size={16} /> {completed ? 'Notice complétée' : 'Notice non complétée'}
        </span>
      </div>
      <section className="panel teacher-detail-card-v2 class-timetable-manager">
  <div className="class-timetable-manager-head">
    <div>
      <p className="eyebrow">EMPLOI DU TEMPS</p>
      <h2>{teacherName(teacher)}</h2>
      <p>
        Importez l'emploi du temps PNG ou JPG de cet enseignant.
        Il apparaîtra automatiquement dans son espace enseignant.
      </p>
    </div>

    <div className="class-timetable-actions">
      <input
        id={`teacher-timetable-upload-${teacherId}`}
        type="file"
        accept="image/png,image/jpeg"
        onChange={uploadTeacherTimetable}
        disabled={timetableUploading}
        hidden
      />

      <label
        htmlFor={`teacher-timetable-upload-${teacherId}`}
        className="button button-primary"
      >
        {timetableUploading
          ? 'Import en cours…'
          : timetableUrl
            ? 'Remplacer le fichier'
            : 'Importer PNG / JPG'}
      </label>

      {timetableUrl && (
        <button
          type="button"
          className="button button-secondary"
          onClick={removeTeacherTimetable}
        >
          Supprimer
        </button>
      )}
    </div>
  </div>

  {timetableMessage && (
    <p className="class-timetable-message">
      {timetableMessage}
    </p>
  )}

  {timetableLoading && (
    <div className="class-empty-state">
      Chargement de l'emploi du temps…
    </div>
  )}

  {!timetableLoading && timetableUrl && (
    <div className="class-timetable-preview">
      <img
        src={timetableUrl}
        alt={`Emploi du temps de ${teacherName(teacher)}`}
      />
    </div>
  )}

  {!timetableLoading && !timetableUrl && (
    <div className="class-empty-state">
      Aucun emploi du temps n'a encore été importé pour cet enseignant.
    </div>
  )}
</section>

      {!notice ? (
        <div className="panel teacher-detail-empty-v2">Cet enseignant n’a pas encore rempli sa notice individuelle.</div>
      ) : (
        <div className="teacher-detail-layout-v2">
          <section className="panel teacher-detail-card-v2">
            <div className="teacher-detail-card-title-v2"><UserRound size={20} /><h2>Identité & coordonnées</h2></div>
            <div className="teacher-admin-detail-grid-v1">
              <div><span>Prénom</span><strong>{displayValue(notice.firstName)}</strong></div>
              <div><span>Nom</span><strong>{displayValue(notice.lastName)}</strong></div>
              <div><span>Genre</span><strong>{genderLabel(notice.gender)}</strong></div>
              <div><span>CIN</span><strong>{displayValue(notice.cin)}</strong></div>
              <div><span>Date de naissance</span><strong>{displayDate(notice.birthDate)}</strong></div>
              <div><span>Lieu de naissance</span><strong>{displayValue(notice.birthPlace)}</strong></div>
              <div className="is-wide"><span>Adresse</span><strong><MapPin size={14} /> {displayValue(notice.address)}</strong></div>
              <div><span>Téléphone</span><strong>{displayValue(notice.phone || teacher.phone)}</strong></div>
              <div><span>Email personnel</span><strong>{displayValue(notice.personalEmail || teacher.personalEmail)}</strong></div>
            </div>
          </section>

          <section className="panel teacher-detail-card-v2">
            <div className="teacher-detail-card-title-v2"><UserRound size={20} /><h2>Situation familiale</h2></div>
            <div className="teacher-admin-detail-grid-v1">
              <div><span>Situation</span><strong>{maritalStatusLabel(notice)}</strong></div>
              <div><span>Nombre d’enfants</span><strong>{displayValue(notice.childrenCount, '0')}</strong></div>
              {notice.maritalStatus === 'married' && <div><span>Nom du conjoint</span><strong>{displayValue(notice.spouseFullName)}</strong></div>}
              {notice.maritalStatus === 'married' && <div><span>Profession du conjoint</span><strong>{displayValue(notice.spouseProfession)}</strong></div>}
            </div>
          </section>

          <section className="panel teacher-detail-card-v2">
            <div className="teacher-detail-card-title-v2"><GraduationCap size={20} /><h2>Formation</h2></div>
            <div className="teacher-admin-detail-grid-v1">
              <div><span>Diplôme</span><strong>{displayValue(notice.diploma)}</strong></div>
              <div><span>Spécialité / domaine</span><strong>{displayValue(notice.studyField)}</strong></div>
              <div><span>Établissement / université</span><strong>{displayValue(notice.institution)}</strong></div>
              <div><span>Année d’obtention</span><strong>{displayValue(notice.graduationYear)}</strong></div>
            </div>
          </section>

          <section className="panel teacher-detail-card-v2 teacher-detail-bank-v2">
            <div className="teacher-detail-card-title-v2"><Banknote size={20} /><h2>Informations administratives & bancaires</h2></div>
            <div className="teacher-admin-detail-grid-v1">
              <div><span>Numéro CNSS</span><strong>{displayValue(notice.cnssNumber)}</strong></div>
              <div><span>Banque</span><strong>{displayValue(notice.bankName)}</strong></div>
              <div className="is-wide"><span>RIB bancaire</span><strong className="teacher-admin-rib-v1">{displayValue(notice.rib)}</strong></div>
            </div>
          </section>
        </div>
      )}

      <section className="panel teacher-detail-card-v2">
        <div className="teacher-detail-card-title-v2"><GraduationCap size={20} /><h2>Affectations pédagogiques</h2></div>
        {assignmentDetails.length ? (
          <div className="teacher-detail-assignments-v2">
            {assignmentDetails.map((item) => <span key={item.id}><strong>{item.className}</strong> · {item.subjectName}</span>)}
          </div>
        ) : <p className="muted">Aucune affectation pédagogique.</p>}
      </section>

      <section className="panel teacher-detail-card-v2">
        <div className="teacher-detail-card-title-v2"><UserRound size={20} /><h2>Compte enseignant</h2></div>
        <div className="teacher-admin-detail-grid-v1">
          <div><span>Nom du compte</span><strong>{teacherName(teacher)}</strong></div>
          <div><span>Identifiant</span><strong>{displayValue(teacher.username)}</strong></div>
          <div><span>Email de connexion</span><strong>{displayValue(teacher.email)}</strong></div>
          <div><span>Téléphone du compte</span><strong>{displayValue(teacher.phone)}</strong></div>
        </div>
      </section>
    </section>
  );
}
